import { useState } from 'react';
import { Menu, X, Sun, Moon, Monitor, LogOut, Settings, Download } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabaseClient } from '../../hooks/useSupabaseClient';
import { useTheme } from '../../hooks/useTheme';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { Button } from '../ui/Button';
import siteContent from '../../data/site-content.json';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const { user, signOut } = useSupabaseClient();
  const { theme, setTheme } = useTheme();
  const { isInstallable, installApp } = usePWAInstall();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleInstall = async () => {
    await installApp();
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor }
  ] as const;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white/80 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <button onClick={handleLogoClick} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">$</span>
              </div>
              <span className="hidden sm:block font-semibold text-gray-900 dark:text-white">
                {siteContent.footer.company}
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          {location.pathname === '/' && (
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors">
                Pricing
              </a>
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors">
                About
              </a>
            </nav>
          )}

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Install Button */}
            {isInstallable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleInstall}
                leftIcon={<Download className="w-4 h-4" />}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Install
              </Button>
            )}

            {/* Theme Selector */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                className="w-9 h-9 p-0"
              >
                {theme === 'light' && <Sun className="w-4 h-4" />}
                {theme === 'dark' && <Moon className="w-4 h-4" />}
                {theme === 'system' && <Monitor className="w-4 h-4" />}
              </Button>

              {themeMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
                  {themeOptions.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => {
                        setTheme(value);
                        setThemeMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center space-x-2 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                      {theme === value && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {user.user_metadata?.full_name || user.email}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  leftIcon={<LogOut className="w-4 h-4" />}
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={handleSignIn}>
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 p-0"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {location.pathname === '/' && (
                <>
                  <a href="#features" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300">
                    Features
                  </a>
                  <a href="#pricing" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300">
                    Pricing
                  </a>
                  <a href="#about" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300">
                    About
                  </a>
                </>
              )}
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                {user ? (
                  <div className="space-y-2">
                    <p className="px-3 text-sm text-gray-600 dark:text-gray-400">
                      {user.user_metadata?.full_name || user.email}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSignOut}
                      className="w-full justify-start"
                      leftIcon={<LogOut className="w-4 h-4" />}
                    >
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleSignIn}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                )}
                
                {/* Mobile Install Button */}
                {isInstallable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleInstall}
                    className="w-full justify-start"
                    leftIcon={<Download className="w-4 h-4" />}
                  >
                    Install App
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close theme menu */}
      {themeMenuOpen && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setThemeMenuOpen(false)} 
        />
      )}
    </header>
  );
}